import { Box, Button, TextField, Typography } from '@mui/material'
import { useState, useEffect } from 'react'
import Modal from './Modal'
import PasswordField from './PasswordField'
import { validatePassword } from '../utils/passwordValidation'
import { api } from '../requests/axios'
import { useNavigate } from 'react-router-dom'

interface PasswordResetModalProps {
  open: boolean
  onClose: () => void
  onSuccess: (email: string, token: string) => void
  initialToken?: string
}

export default function PasswordResetModal({ open, onClose, onSuccess, initialToken }: PasswordResetModalProps) {
  const [email, setEmail] = useState("")
  const [password, setPassword] = useState("")
  const [passwordConfirm, setPasswordConfirm] = useState("")
  const [token, setToken] = useState(initialToken || "")
  const [passwordError, setPasswordError] = useState<string[]>([])
  const [passwordConfirmError, setPasswordConfirmError] = useState("")
  const [emailSent, setEmailSent] = useState(false)
  const navigate = useNavigate()

  useEffect(() => {
    if (initialToken) {
      setToken(initialToken)
    }
  }, [initialToken])

  const handlePasswordResetRequest = async () => {
    try {
      const response = await api.post(
        (process.env.REACT_APP_API_PROXY_URL || '').concat(`/passwords.json`),
        { password: { email: email } }
      )
      if (response.status === 200) {
        setEmailSent(true)
      }
    } catch (error: any) {
      // Don't reveal if email exists for security
      setEmailSent(true)
    }
  }

  const handlePasswordReset = async () => {
    const passwordValidationErrors = validatePassword(password)
    setPasswordError(passwordValidationErrors)

    if (password !== passwordConfirm) {
      setPasswordConfirmError('Passwords do not match')
      return
    } else {
      setPasswordConfirmError('')
    }

    if (passwordValidationErrors.length > 0) {
      return
    }

    try {
      const response = await api.put(
        (process.env.REACT_APP_API_PROXY_URL || '').concat(`/passwords/update.json?token=${token}`),
        { password_reset: { password: password } }
      )
      const data = await response.data

      if (response.status === 200) {
        onSuccess(data.email, data.authentication_token)
        navigate("/color-charts")
      }
    } catch (error: any) {
      const errorMessage = error?.response?.data?.error || 'Password reset failed. Please try again.'
      alert(errorMessage)
    }
  }

  const handleClose = () => {
    setEmail("")
    setPassword("")
    setPasswordConfirm("")
    setToken("")
    setPasswordError([])
    setPasswordConfirmError("")
    setEmailSent(false)
    onClose()
  }

  if (!open) return null

  return (
    <Modal title="Reset Password" onClose={handleClose}>
      <Box sx={{padding: 3}}>
        {!emailSent && !token ? (
          <>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
              Enter your email address and we'll send you a link to reset your password.
            </Typography>
            <TextField
              id="reset-email"
              label="Email"
              type="email"
              variant="outlined"
              fullWidth
              required
              sx={{ mb: 2 }}
              value={email}
              onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
                setEmail(event.target.value);
              }}
              onKeyDown={(event: React.KeyboardEvent<HTMLInputElement>) => {
                if (event.key === 'Enter' && email.trim() !== '') {
                  handlePasswordResetRequest();
                }
              }}
            />
            <Button 
              onClick={handlePasswordResetRequest}
              disabled={email.trim() === ''}
              fullWidth
            >
              Send Reset Link
            </Button>
          </>
        ) : emailSent && !token ? (
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            If an account exists with that email, you'll receive password reset instructions shortly.
          </Typography>
        ) : (
          <>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
              Enter your new password below.
            </Typography>
            <PasswordField
              id="reset-password"
              label="New Password"
              value={password}
              onChange={(value, errors) => {
                setPassword(value)
                setPasswordError(errors)
                // Check password match when password changes
                if (passwordConfirm && value !== passwordConfirm) {
                  setPasswordConfirmError('Passwords do not match')
                } else {
                  setPasswordConfirmError('')
                }
              }}
            />
            <TextField
              id="reset-password-confirm"
              label="Confirm New Password"
              type="password"
              variant="outlined"
              fullWidth
              required
              sx={{ mb: 2 }}
              error={!!passwordConfirmError}
              helperText={passwordConfirmError}
              value={passwordConfirm}
              onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
                const value = event.target.value;
                setPasswordConfirm(value);
                if (value && value !== password) {
                  setPasswordConfirmError('Passwords do not match');
                } else {
                  setPasswordConfirmError('');
                }
              }}
              onKeyDown={(event: React.KeyboardEvent<HTMLInputElement>) => {
                if (event.key === 'Enter') {
                  handlePasswordReset();
                }
              }}
            />
            <Button 
              onClick={handlePasswordReset}
              disabled={password.trim() === '' || passwordConfirm.trim() === '' || passwordError.length > 0 || passwordConfirmError !== ''}
              fullWidth
            >
              Reset Password
            </Button>
          </>
        )}
      </Box>
    </Modal>
  )
}

